import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { styled } from '@mui/material/styles';
import Loader from 'Components/Forms/Loader';
import { useQuery, useMutation } from '@apollo/client';
import { GET_COMPANY_DETAILS } from 'graphql/secure/queries';
import useMessages from 'hooks/useMessages';
import ThemeButton from 'DesignSystem/Buttons/ThemeButton/ThemeButton';
import { UPDATE_COMPANY } from 'graphql/secure/mutations/company.mutations';
import ErrorState from 'Components/ErrorState';
import CompanyFormCombined from 'FeatureModules/Setting/Components/Forms/CompanyFormCombined';

interface ICompanyDataDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

const StyledActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2, 0, 0),
}));

const CompanyDataDialog: React.FC<ICompanyDataDialogProps> = ({ open, onClose, onSubmit }) => {
  const { data: { company } = {}, error, loading } = useQuery(GET_COMPANY_DETAILS);
  const { setSuccessMessage, setErrorMessage } = useMessages();

  const [updateCompany] = useMutation(UPDATE_COMPANY, {
    onCompleted: () => {
      setSuccessMessage('Success! Company details were updated.');
      onSubmit();
    },
    onError: () => {
      setErrorMessage('Something went wrong updating your companies details.');
    },
  });

  const handleSubmit = (input: unknown) => {
    return updateCompany({
      variables: {
        input,
      },
    });
  };

  if (error) {
    return <ErrorState error="Something went wrong fetching your company data." />;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="form-dialog-title"
      data-testid="dialog-update-company-details"
    >
      {loading && <Loader overlay />}
      <DialogTitle id="form-dialog-title">Update company data</DialogTitle>
      <DialogContent>
        <CompanyFormCombined onSubmit={handleSubmit} company={company}>
          {({ isSubmitting }) => (
            <StyledActions>
              <ThemeButton color="WHITE" size="large" onClick={onClose}>
                Cancel
              </ThemeButton>
              <ThemeButton
                loading={isSubmitting}
                type="submit"
                color="PRIMARY"
                size="large"
                autoFocus
                data-testid="submit-company-form"
              >
                Submit
              </ThemeButton>
            </StyledActions>
          )}
        </CompanyFormCombined>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyDataDialog;
